import { createAsyncThunk } from "@reduxjs/toolkit";
import { postLoginService } from "../../Services";
import { alert } from "../../utils/commonFunctions/alert";

const toNumericId = (value) => {
    if (value == null || value === "") return null;
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) return null;
    return parsed;
};

const normalizeIdArray = (values) => {
    if (!Array.isArray(values)) return [];
    return values
        .map((value) => {
            const rawValue = value && typeof value === "object" ? value.id : value;
            return toNumericId(rawValue);
        })
        .filter((value) => value !== null);
};

const normalizeDealTerms = (terms) => {
    if (!Array.isArray(terms)) return [];

    return terms
        .map((term) => {
            let parsedTerm = term;

            if (typeof parsedTerm === "string") {
                try {
                    parsedTerm = JSON.parse(parsedTerm);
                } catch {
                    parsedTerm = term;
                }
            }

            const termId =
                typeof parsedTerm === "object" && parsedTerm !== null
                    ? toNumericId(parsedTerm.id)
                    : toNumericId(parsedTerm);

            if (termId === null) return null;

            if (parsedTerm && typeof parsedTerm === "object") {
                const termText =
                    typeof parsedTerm.term === "string"
                        ? parsedTerm.term.trim()
                        : typeof parsedTerm.name === "string"
                            ? parsedTerm.name.trim()
                            : typeof parsedTerm.title === "string"
                                ? parsedTerm.title.trim()
                                : "";

                return termText ? { id: termId, term: termText } : { id: termId };
            }

            return { id: termId };
        })
        .filter(Boolean);
};

const normalizeText = (value) => {
    if (typeof value !== "string") return "";
    return value.trim();
};

const enrichProposalTerms = (payload, termCatalog) => {
    const proposals = Array.isArray(payload?.proposals) ? payload.proposals : [];
    if (!proposals.length || !Array.isArray(termCatalog) || !termCatalog.length) {
        return payload;
    }

    const termMap = new Map(
        termCatalog
            .map((term) => {
                const id = toNumericId(term?.id);
                const text = normalizeText(term?.term || term?.name || term?.title);
                if (id === null || !text) return null;
                return [id, text];
            })
            .filter(Boolean)
    );

    if (!termMap.size) return payload;

    const enrichedProposals = proposals.map((proposal) => {
        const dealTerms = proposal?.deal?.dealTerms;
        if (!Array.isArray(dealTerms) || !dealTerms.length) return proposal;

        const normalizedTerms = dealTerms.map((term) => {
            let parsedTerm = term;

            if (typeof parsedTerm === "string") {
                const trimmed = parsedTerm.trim();
                if (trimmed.startsWith("{")) {
                    try {
                        parsedTerm = JSON.parse(trimmed);
                    } catch {
                        parsedTerm = term;
                    }
                }
            }

            if (parsedTerm && typeof parsedTerm === "object") {
                const id = toNumericId(parsedTerm.id);
                const text = normalizeText(parsedTerm.term || parsedTerm.name || parsedTerm.title || parsedTerm.label);
                if (id !== null && !text && termMap.has(id)) {
                    return { ...parsedTerm, term: termMap.get(id) };
                }
                return parsedTerm;
            }

            const scalarId = toNumericId(parsedTerm);
            if (scalarId !== null && termMap.has(scalarId)) {
                return { id: scalarId, term: termMap.get(scalarId) };
            }

            return parsedTerm;
        });

        return {
            ...proposal,
            deal: {
                ...proposal.deal,
                dealTerms: normalizedTerms,
            },
        };
    });

    return {
        ...payload,
        proposals: enrichedProposals,
    };
};

const extractApiErrorMessage = (error, fallbackMessage) => {
    const nestedMessage = error?.response?.data?.error?.message;

    if (Array.isArray(nestedMessage) && nestedMessage.length > 0) {
        return nestedMessage[0];
    }

    if (typeof nestedMessage === "string" && nestedMessage.trim()) {
        return nestedMessage;
    }

    return error?.response?.data?.message || error?.message || fallbackMessage;
};

// Helper: build FormData for influencer proposal (shared by create, draft create, draft update)
const buildProposalFormData = (dealData) => {
    const formData = new FormData();
    const normalizedCategoryIds = normalizeIdArray(dealData.categoryIds);
    const normalizedDealTerms = normalizeDealTerms(dealData.dealTerms);

    if (dealData.merchantId) formData.append("merchantId", dealData.merchantId);
    if (dealData.title) formData.append("title", dealData.title);
    if (dealData.description) formData.append("description", dealData.description);
    // Optional — only send when non-empty so it defaults to the description.
    if (dealData.kitchenDescription && dealData.kitchenDescription.trim())
        formData.append("kitchenDescription", dealData.kitchenDescription);
    if (dealData.dealTypeId) formData.append("dealTypeId", dealData.dealTypeId);
    if (dealData.originalPrice != null) formData.append("originalPrice", dealData.originalPrice);
    if (dealData.dealPrice != null) formData.append("dealPrice", dealData.dealPrice);
    formData.append("isFree", dealData.isFree ? "true" : "false");
    if (dealData.totalQuantity != null) formData.append("totalQuantity", dealData.totalQuantity);
    formData.append("isPublic", dealData.isPublic ? "true" : "false");
    formData.append("isOngoing", dealData.isOngoing ? "true" : "false");
    if (dealData.fulfillmentType) formData.append("fulfillmentType", dealData.fulfillmentType);
    if (dealData.availableFrom) formData.append("availableFrom", dealData.availableFrom);
    if (!dealData.isOngoing && dealData.availableUntil) formData.append("availableUntil", dealData.availableUntil);
    if (normalizedCategoryIds.length) {
        formData.append("categoryIds", JSON.stringify(normalizedCategoryIds));
    }
    if (normalizedDealTerms.length) {
        formData.append("dealTerms", JSON.stringify(normalizedDealTerms));
    }
    // Redemption fields
    if (dealData.redemptionType) formData.append("redemptionType", dealData.redemptionType);
    if (dealData.redemptionDays != null && dealData.redemptionDays !== "") {
        formData.append("redemptionDays", dealData.redemptionDays);
    }
    if (dealData.redemptionDate) {
        const rd = new Date(dealData.redemptionDate);
        if (!isNaN(rd.getTime())) formData.append("redemptionDate", rd.toISOString());
    }
    // Per-user purchase limits — always sent so blanking clears to null ("" → null).
    formData.append("maxOrdersPerWindow", dealData.maxOrdersPerWindow == null ? "" : dealData.maxOrdersPerWindow);
    formData.append("orderWindowDays", dealData.orderWindowDays == null ? "" : dealData.orderWindowDays);
    formData.append("maxQtyPerOrder", dealData.maxQtyPerOrder == null ? "" : dealData.maxQtyPerOrder);
    formData.append("oneTimeOnly", dealData.oneTimeOnly ? "true" : "false");
    if (dealData.image) {
        formData.append("image", dealData.image);
    } else if (dealData.existingImageUrl) {
        formData.append("existingImagePath", dealData.existingImageUrl);
    }
    return formData;
};


export const createDealProposal = createAsyncThunk(
    "influencerDealProposal/createDealProposal",
    async (dealData, { rejectWithValue }) => {
        try {
            const url = `/api/influencers/deals`;
            const normalizedCategoryIds = normalizeIdArray(dealData.categoryIds);
            const normalizedDealTerms = normalizeDealTerms(dealData.dealTerms);

            // Create FormData for file upload
            const formData = new FormData();

            // Required fields
            formData.append("merchantId", dealData.merchantId);
            formData.append("title", dealData.title);
            formData.append("description", dealData.description);
            if (dealData.kitchenDescription && dealData.kitchenDescription.trim())
                formData.append("kitchenDescription", dealData.kitchenDescription);
            formData.append("dealTypeId", dealData.dealTypeId);
            formData.append("originalPrice", dealData.originalPrice);
            formData.append("dealPrice", dealData.dealPrice);

            // Optional fields
            if (dealData.totalQuantity) {
                formData.append("totalQuantity", dealData.totalQuantity);
            }

            formData.append("isFree", dealData.isFree ? "true" : "false");
            formData.append("isPublic", dealData.isPublic ? "true" : "false");
            formData.append("isOngoing", dealData.isOngoing ? "true" : "false");

            if (dealData.fulfillmentType) {
                formData.append("fulfillmentType", dealData.fulfillmentType);
            }

            // Dates
            if (dealData.availableFrom) {
                formData.append("availableFrom", dealData.availableFrom);
            }
            if (!dealData.isOngoing && dealData.availableUntil) {
                formData.append("availableUntil", dealData.availableUntil);
            }

            // Categories
            if (normalizedCategoryIds.length > 0) {
                formData.append("categoryIds", JSON.stringify(normalizedCategoryIds));
            }

            // Deal Terms - ensure proper format (array of objects, not strings)
            if (normalizedDealTerms.length > 0) {
                formData.append("dealTerms", JSON.stringify(normalizedDealTerms));
            }

            // Redemption fields
            if (dealData.redemptionType) formData.append("redemptionType", dealData.redemptionType);
            if (dealData.redemptionDays != null && dealData.redemptionDays !== "") {
                formData.append("redemptionDays", dealData.redemptionDays);
            }
            if (dealData.redemptionDate) {
                const rd = new Date(dealData.redemptionDate);
                if (!isNaN(rd.getTime())) formData.append("redemptionDate", rd.toISOString());
            }

            // Per-user purchase limits (blank/off = no limit; backend maps "" → null)
            formData.append("maxOrdersPerWindow", dealData.maxOrdersPerWindow == null ? "" : dealData.maxOrdersPerWindow);
            formData.append("orderWindowDays", dealData.orderWindowDays == null ? "" : dealData.orderWindowDays);
            formData.append("maxQtyPerOrder", dealData.maxQtyPerOrder == null ? "" : dealData.maxQtyPerOrder);
            formData.append("oneTimeOnly", dealData.oneTimeOnly ? "true" : "false");

            // Link/clean up the server-side draft if one was created during editing
            if (dealData.draftId) {
                formData.append("draftId", dealData.draftId);
            }

            // Image
            if (dealData.image) {
                // New file uploaded by user — send as binary
                formData.append("image", dealData.image);
            } else if (dealData.existingImageUrl) {
                // Resubmit with no new upload — pass the relative path so the backend
                // can reference the existing file (requires backend to support this field).
                formData.append("existingImagePath", dealData.existingImageUrl);
            }

            // Additional images
            if (dealData.images && dealData.images.length > 0) {
                formData.append("images", JSON.stringify(dealData.images));
            }

            // console.log("CREATE DEAL PROPOSAL PAYLOAD---->", {
            //     merchantId: dealData.merchantId,
            //     title: dealData.title,
            //     description: dealData.description,
            //     dealTypeId: dealData.dealTypeId,
            //     categoryIds: normalizedCategoryIds,
            //     originalPrice: dealData.originalPrice,
            //     dealPrice: dealData.dealPrice,
            //     hasImage: !!dealData.image,
            // });

            const response = await postLoginService.post(url, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            // console.log("CREATE DEAL PROPOSAL RESPONSE---->", response);

            if (response?.success) {
                // alert.success(response?.message || "Deal proposal sent successfully!");
                return response.data;
            } else {
                alert.error(response?.message || "Failed to send deal proposal");
                return rejectWithValue(response?.message || "Failed to send deal proposal");
            }
        } catch (error) {
            // The backend nests a detailed message for conflict cases:
            // error.response.data.error.error = "You already have a pending proposal..."
            const detailedError = error?.response?.data?.error?.error;
            const errorMessage = extractApiErrorMessage(error, "Failed to send deal proposal");

            if (detailedError) {
                // Don't fire the generic toast — the UI will show a conflict modal instead
                return rejectWithValue({ type: "CONFLICT", message: detailedError });
            }

            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Get Influencer's Deal Proposals
export const getInfluencerDealProposals = createAsyncThunk(
    "influencerDealProposal/getInfluencerDealProposals",
    async (params = {}, { rejectWithValue }) => {
        try {
            const { status, sortBy, sortOrder, page = 1 } = params;

            // Build query params
            const queryParams = new URLSearchParams();

            if (status) {
                queryParams.append("status", status);
            }
            if (sortBy) {
                queryParams.append("sortBy", sortBy);
            }
            if (sortOrder) {
                queryParams.append("sortOrder", sortOrder);
            }
            queryParams.append("page", page);

            const url = `/api/influencers/deal-proposals?${queryParams.toString()}`;

            const response = await postLoginService.get(url);
            // console.log("GET INFLUENCER DEAL PROPOSALS RESPONSE---->", response);

            if (response?.success) {
                let enrichedPayload = response.data;

                try {
                    const termsResponse = await postLoginService.get(`/api/deal-terms/merchant`);
                    if (termsResponse?.success) {
                        const termCatalog = termsResponse?.data?.data || termsResponse?.data || [];
                        enrichedPayload = enrichProposalTerms(response.data, termCatalog);
                    }
                } catch (_error) {
                }

                return enrichedPayload;
            } else {
                alert.error(response?.message || "Failed to fetch deal proposals");
                return rejectWithValue(
                    response?.message || "Failed to fetch deal proposals"
                );
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to fetch deal proposals";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Withdraw Deal Proposal
export const withdrawDealProposal = createAsyncThunk(
    "influencerDealProposal/withdrawDealProposal",
    async (proposalId, { rejectWithValue }) => {
        try {
            const url = `/api/influencers/deal-proposals/${proposalId}`;
            const response = await postLoginService.delete(url);
            // console.log("WITHDRAW DEAL PROPOSAL RESPONSE---->", response);

            if (response?.success) {
                // alert.success(response?.message || "Proposal withdrawn successfully");
                return response.data;
            } else {
                alert.error(response?.message || "Failed to withdraw proposal");
                return rejectWithValue(response?.message || "Failed to withdraw proposal");
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to withdraw proposal";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Create a new influencer deal proposal draft (saves partial data server-side)
export const createInfluencerProposalDraft = createAsyncThunk(
    "influencerDealProposal/createInfluencerProposalDraft",
    async (dealData, { rejectWithValue }) => {
        try {
            const url = `/api/influencers/deals/drafts`;
            const formData = buildProposalFormData(dealData);
            const response = await postLoginService.post(url, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (response?.success) {
                return response?.data?.deal || response.data;
            }
            return rejectWithValue(response?.message || "Failed to save draft");
        } catch (error) {
            const errorMessage = extractApiErrorMessage(error, "Failed to save draft");
            return rejectWithValue(errorMessage);
        }
    }
);

// Update an existing influencer deal proposal draft
export const updateInfluencerProposalDraft = createAsyncThunk(
    "influencerDealProposal/updateInfluencerProposalDraft",
    async ({ draftId, dealData }, { rejectWithValue }) => {
        try {
            const url = `/api/influencers/deals/drafts/${draftId}`;
            const formData = buildProposalFormData(dealData);
            const response = await postLoginService.put(url, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (response?.success) {
                return response?.data?.deal || response.data;
            }
            return rejectWithValue(response?.message || "Failed to update draft");
        } catch (error) {
            const errorMessage = extractApiErrorMessage(error, "Failed to update draft");
            return rejectWithValue(errorMessage);
        }
    }
);

// Toggle pause/resume on an approved deal
export const togglePauseDeal = createAsyncThunk(
    "influencerDealProposal/togglePauseDeal",
    async ({ dealId, isPaused }, { rejectWithValue }) => {
        try {
            const url = `/api/influencers/deals/${dealId}/pause`;
            const response = await postLoginService.patch(url, { isPaused });
            if (response?.success) {
                alert.success(response?.message || (isPaused ? "Deal paused successfully" : "Deal resumed successfully"));
                return { dealId, isPaused };
            } else {
                alert.error(response?.message || "Failed to update deal status");
                return rejectWithValue(response?.message || "Failed to update deal status");
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to update deal status";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Fetch all influencer proposal drafts
export const fetchInfluencerDrafts = createAsyncThunk(
    "influencerDealProposal/fetchInfluencerDrafts",
    async (_, { rejectWithValue }) => {
        try {
            const response = await postLoginService.get(`/api/influencers/deals/drafts`);
            if (response?.success) {
                return response.data;
            } else {
                return rejectWithValue(response?.message || "Failed to fetch drafts");
            }
        } catch (error) {
            const errorMessage = extractApiErrorMessage(error, "Failed to fetch drafts");
            return rejectWithValue(errorMessage);
        }
    }
);

// Delete an influencer proposal draft
export const deleteInfluencerDraft = createAsyncThunk(
    "influencerDealProposal/deleteInfluencerDraft",
    async (draftId, { rejectWithValue }) => {
        try {
            const response = await postLoginService.delete(`/api/influencers/deals/drafts/${draftId}`);
            if (response?.success) {
                // alert.success(response?.data?.message || response?.message || "Draft deleted successfully");
                return draftId;
            } else {
                return rejectWithValue(response?.message || "Failed to delete draft");
            }
        } catch (error) {
            const errorMessage = extractApiErrorMessage(error, "Failed to delete draft");
            return rejectWithValue(errorMessage);
        }
    }
);
